import { Vue, Component, Inject, Provide, namespace, Watch } from 'nuxt-property-decorator';
import {
  FormDto,
  FormFieldChoiceDto,
  BillingInfoDto,
  EventDto,
  isSuccess,
  isValidationError,
  PostFormEntryResultDto,
  FormEntryDto,
  QuoteDto,
  getDiscrepancies,
  QuoteItem,
  fillDictionary,
  FormType,
  getEmail,
  FormState,
  SubmissionDecisionEnum,
  Dictionary,
  PaymentMethod,
} from 'fourwaves-shared';
import {
  getRequiredFieldsWithNoAvailableChoices,
  getAllPaidChoices,
} from 'fourwaves-shared/src/services/fourwaves/models/FormDto';
import { TaxRules } from 'fourwaves-shared/src/services';
import { IApiService } from '~/services';
import { EXTERNAL_BILLING_INFO, EXTERNAL_PAYMENT_ID, FORM_CONTEXT } from '~/types/cookies';
const EventsModule = namespace('events');

@Component({
  // Remove redundant unit test warnings.
  template: '<p/>',
  validate({ query }) {
    if (query.id) {
      return /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/.test(query.id as string);
    }
    return true;
  },
})
export default class FormPageMixin extends Vue {
  @Provide('apiService') $api!: IApiService;
  @Inject() $validator;

  @EventsModule.Getter currentEvent!: EventDto;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.Getter isChair!: boolean;
  @EventsModule.State withUnlimitedSubmissions!: boolean;
  @EventsModule.Action fetchEvent!: () => Promise<EventDto | null>;

  type: FormType | null = null;
  form: FormDto = <FormDto>{};
  formEntry: FormEntryDto | null = null;
  formData: Dictionary<string | string[]> = {};

  quote: QuoteDto | null = null;
  priceDiscrepancies: QuoteItem[] = [];

  fromExtPaymentPlatform = false;

  billingInfo: BillingInfoDto | null = null;
  paymentMethod: PaymentMethod | null = null;
  paymentGatewayToken: string | null = null;
  paymentGatewayAuthToken: string | null = null;
  serviceResult: PostFormEntryResultDto | null = null;
  timestamp = '';
  couponCode = '';
  nameOnCard: string | null = null;

  formEntryEmail = '';

  @Watch('paidChoices')
  async onPaidChoicesUpdate(newChoices, oldChoices) {
    // Do not fetch quote when there are no changes
    const needsUpdate = JSON.stringify(newChoices) !== JSON.stringify(oldChoices);
    if (!needsUpdate) {
      return;
    }

    // Do not show prices
    if (newChoices && newChoices.length === 0) {
      this.quote = null;
      return;
    }

    if (!this.$wait.is('get-quote')) {
      this.quote = await this.fetchNewQuote();
    }
  }

  @Watch('countryRegion')
  async onContryRegionChanged() {
    this.quote = await this.fetchNewQuote();
  }

  @Watch('stateProvince')
  async onStateProvinceChanged() {
    this.quote = await this.fetchNewQuote();
  }

  @Watch('paymentMethod')
  async onPaymentMethodChanged(paymentMethod: PaymentMethod) {
    if (paymentMethod === PaymentMethod.Void) await this.onCouponCodeUpdate('');
    else this.quote = await this.fetchNewQuote();
  }

  @Watch('vatNumber')
  async onVatNumberChanged(newVatNumber: string, oldVatNumber: string) {
    const wasEmpty = !oldVatNumber || oldVatNumber.trim() === '';
    const isEmpty = !newVatNumber || newVatNumber.trim() === '';
    if (wasEmpty !== isEmpty) this.quote = await this.fetchNewQuote();
  }

  get currentPage(): string {
    return this.$route?.path ? this.$route.path : '';
  }

  get isFormDisplayed(): boolean {
    // hidden when no form
    if (!this.form) return false;

    // displayed when organizer
    if (this.isOrganizer) return true;

    // displayed when registration edit
    if (this.form.type === FormType.Registration && this.formEntry) return true;

    // hidden when not open AND waiting list closed
    if (!this.formEntry && this.form.state !== FormState.Open && this.form.state !== FormState.FullWithWaitingList)
      return false;

    return true;
  }

  get blockingMessage() {
    if (this.form?.formSections && !this.isOrganizer) {
      // block when state is full and not in edit mode
      if (this.form.state === FormState.Full && !this.formEntry) {
        return this.$t('form.summary.blocked_edit');
      }

      // block when state is not open and submission is not accepted
      if (
        this.form.type === FormType.Submission &&
        this.form.state === FormState.PeriodHasEnded &&
        this.formEntry &&
        !this.isChair
      ) {
        if (!this.currentEvent.areSubmissionsAlwaysEditableWhenAccepted) {
          return this.$t('form.summary.blocked_edit');
        }
        const isAccepted = this.formEntry.submissionDecisions?.some(
          d => d.decision === SubmissionDecisionEnum.Accepted,
        );
        if (!isAccepted) {
          return this.$t('form.summary.blocked_edit');
        }
      }

      // block when registration period is not live
      if (
        this.form.type === FormType.Registration &&
        (this.form.state === FormState.PeriodHasEnded ||
          this.form.state === FormState.EventHasEnded ||
          this.form.state === FormState.PeriodDidNotStart)
      ) {
        return this.$t('form.summary.blocked_edit');
      }
    }

    // check ongoing file uploads
    if (this.form?.formSections.flatMap(x => x.formFields).some(x => this.$wait.is(`file-upload-${x.id}`))) {
      return this.$t('form.summary.ongoing_upload_notice');
    }

    // check required checkboxes/radios availability
    const requiredMaxQuantityError = this.errors?.items?.find(err => err.rule === 'required_maxQuantity');

    if (requiredMaxQuantityError && !this.isOrganizer) {
      return requiredMaxQuantityError.msg;
    }

    return '';
  }

  get paidChoices(): FormFieldChoiceDto[] {
    return getAllPaidChoices(this.form, this.formData);
  }

  get confirmationMessage() {
    if (this.formEntry) {
      return this.$t('form.edit_success');
    }

    if (this.form.state === FormState.FullWithWaitingList) {
      return this.$t('form.breadcrumb.waitlist_confirmation');
    }

    return this.form.type === FormType.Registration
      ? this.$t('form.breadcrumb.registration_confirmation_header')
      : this.$t('form.breadcrumb.submission_confirmation_header');
  }

  get countryRegion(): string {
    return this.billingInfo && this.billingInfo.civicAddress ? this.billingInfo.civicAddress.countryRegion : '';
  }

  get stateProvince(): string {
    return this.billingInfo && this.billingInfo.civicAddress ? this.billingInfo.civicAddress.stateProvince : '';
  }

  get vatNumber(): string {
    return this.billingInfo ? this.billingInfo.vatNumber || '' : '';
  }

  get isFree(): boolean {
    return this.quote?.total === 0 && this.couponCode?.length > 0;
  }

  get hasVat(): boolean {
    if (!this.quote) return false;
    if (this.quote.vat > 0) return true;
    return this.quote.quoteItems
      .flatMap(x => x.taxRuleAsString)
      .some(
        rule =>
          rule === TaxRules.Vat0 ||
          rule === TaxRules.VatReverseCharge0 ||
          rule === TaxRules.Vat21AllSpainWithVatNumber0,
      );
  }

  async created() {
    this.$wait.start('fetch');

    this.form = this.currentEvent.forms.find(x => x.type === this.type)!;

    if (this.$route.query.id) {
      this.formEntry = await this.$api.getFormEntry(this.$route.query.id as string, true);

      if (!this.formEntry) {
        return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '404' } }));
      } else if (!this.$auth.loggedIn) {
        return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '401' } }));
      } else if (!this.canUserAccessEntry(this.formEntry)) {
        return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '403' } }));
      }

      this.formEntryEmail = getEmail(this.formEntry, this.form);
      fillDictionary(this.formEntry, this.formData);
    }

    if (!this.isOrganizer) {
      getRequiredFieldsWithNoAvailableChoices(this.form, this.formData).forEach(fieldId => {
        this.$validator.errors.add({
          field: fieldId,
          msg: this.$t('shared.error.server_validation.required_maxQuantity'),
          rule: 'required_maxQuantity',
        });
      });
    }

    // Feed context when returning from an external payment platform
    const rawFormContext = localStorage.getItem(FORM_CONTEXT);
    if (rawFormContext) {
      localStorage.removeItem(FORM_CONTEXT);

      const context = JSON.parse(rawFormContext);
      this.fromExtPaymentPlatform = true;
      this.quote = context.quote;
      this.billingInfo = context.billingInfo;
      this.paymentMethod = context.paymentMethod;
      this.timestamp = context.timestamp;
      this.couponCode = context.couponCode;
      this.formData = context.formData;
      await this.$nextTick();

      const cookieParams = this.$config.API_URL.includes('localhost')
        ? undefined
        : { domain: '.fourwaves.com', path: '/' };

      if (this.$cookies.get(EXTERNAL_BILLING_INFO)) {
        this.billingInfo = this.$cookies.get<BillingInfoDto>(EXTERNAL_BILLING_INFO, { parseJSON: true });
        this.$cookies.remove(EXTERNAL_BILLING_INFO, cookieParams);
      }

      if (this.$cookies.get(EXTERNAL_PAYMENT_ID)) {
        this.paymentGatewayToken = this.$cookies.get(EXTERNAL_PAYMENT_ID);
        this.$cookies.remove(EXTERNAL_PAYMENT_ID, cookieParams);
        await this.nextStep();
      }
    } else {
      this.refreshTimestamp();
    }

    this.$wait.end('fetch');
  }

  public async fetchNewQuote(force = false): Promise<QuoteDto | null> {
    this.$wait.start('get-quote');

    if (force) {
      this.fromExtPaymentPlatform = false;
    }

    if (this.fromExtPaymentPlatform) {
      // compare the prices with those obtained before going to the external payment platform
      this.$wait.end('get-quote');
      return this.quote;
    }

    const quote = await this.$api.getQuote(
      this.form.id,
      this.formData,
      this.formEntry != null ? this.formEntry.id! : null,
      this.countryRegion,
      this.stateProvince,
      this.couponCode,
      this.vatNumber,
      this.paymentMethod,
    );
    this.$wait.end('get-quote');
    return quote;
  }

  public async nextStep() {
    this.$wait.start('on-next-step');
    try {
      await this.innerNextStep();
    } catch (e: any) {
      console.error(e);
      if (e && e.code === '9003') {
        // do not log Paysafe validation error
      } else {
        this.$sentry.captureException(e);
      }
    } finally {
      this.$wait.end('on-next-step');
    }
  }

  public async innerNextStep() {
    // Validate current form
    this.$validator.errors.remove('CouponCode');
    const isValid = this.$validator.errors.items.length === 0 && (await this.$validator.validateAll());
    if (!isValid) {
      this.scrollToErrors();
      return;
    }

    // Check for price discrepancies
    const newQuote = await this.fetchNewQuote(true);
    if (newQuote != null) {
      this.priceDiscrepancies = getDiscrepancies(this.quote, newQuote);
      this.quote = newQuote;
      if (this.priceDiscrepancies.length > 0) {
        this.paymentGatewayToken = null;
        await this.fetchEventAndRefreshForm();
        return;
      }
    }

    // Show billing form when needed
    if (this.quote && this.quote.quoteItems.length > 0 && !this.$route.name!.includes('billing')) {
      await this.goToBillingPage();
      return;
    }

    // Change payment method with the use of a 100% discount coupon code
    this.$validator.errors.remove('CouponCode');
    if (this.isFree) {
      this.paymentMethod = PaymentMethod.Void;
    }

    // Initiate platform-specific payment flow
    if (
      this.paymentMethod === PaymentMethod.Stripe ||
      this.paymentMethod === PaymentMethod.Paysafe ||
      this.paymentMethod === PaymentMethod.GlobalPayments ||
      this.paymentMethod === PaymentMethod.Moneris ||
      this.paymentMethod === PaymentMethod.PayPalCardFields
    ) {
      const currentStep = this.$refs.currentStep as any;
      if (currentStep && currentStep.initiatePayment) {
        this.$wait.start('payment');
        this.paymentGatewayToken = await currentStep.initiatePayment();

        if (!this.paymentGatewayToken) {
          this.$wait.end('payment');
          // Triger validation error
          return;
        }
      }
    }

    // Form edit - Validate that the user really wants to modify the email
    if (this.formEntryEmail && this.form.emailFormFieldId) {
      const newEmail = this.formData[this.form.emailFormFieldId] as string;
      if (this.formEntryEmail !== newEmail) {
        let message = this.$t('form.edit_email') as string;
        message = message.replace('{oldEmail}', this.formEntryEmail);
        message = message.replace('{newEmail}', newEmail);
        if (!window.confirm(message)) {
          return;
        }
        this.formEntryEmail = newEmail as string;
      }
    }

    // Payload clean-up (do not trigger back-end validation when unneeded)
    if (!this.quote || this.quote.quoteItems.length === 0) {
      this.billingInfo = null;
    }

    // Submit form to server
    const result = await this.$api.postFormEntry(
      this.form.id,
      this.formEntry != null ? this.formEntry.id! : null,
      this.formData,
      this.billingInfo,
      this.timestamp,
      this.paymentMethod === PaymentMethod.PayPalButtons || this.paymentMethod === PaymentMethod.PayPalCardFields
        ? PaymentMethod.PayPal
        : this.paymentMethod,
      this.paymentGatewayToken
        ? {
            token: this.paymentGatewayToken,
            authenticationToken: this.paymentGatewayAuthToken,
          }
        : null,
      this.nameOnCard,
      undefined,
      undefined,
      this.couponCode,
    );

    this.$wait.end('payment');

    if (isSuccess(result)) {
      this.serviceResult = result;

      if (this.$auth.loggedIn) await this.$auth.fetchUser();

      if (!this.formEntry || (this.quote && this.quote.quoteItems.length > 0)) {
        // Creation mode OR with payment
        await this.goToConfirmationPage();
      } else {
        // Edit mode
        this.$toast.success(this.$t('form.edit_success') as string);
      }
    } else if (isValidationError(result)) {
      await this.fetchEventAndRefreshForm();
      this.refreshTimestamp();

      if (result.errors[''] && result.errors[''].some(x => x.code === 'COUPON_CODE_INVALID')) {
        this.couponCode = '';
        const newQuote = await this.fetchNewQuote();
        if (newQuote != null) {
          this.quote = newQuote;
        }
      }

      // Special payment gateway flows
      if (result.errors[''] && result.errors[''].some(x => x.code === 'PAYMENT_REQUIRES_ACTION')) {
        // Stripe 3D secure / Stripe authentication
        if (this.paymentMethod === PaymentMethod.Stripe) {
          const clientSecret = result.errors[''].pop();
          if (clientSecret) {
            this.$eventBus.emit('initStripeAuthentication', clientSecret.code);
            this.$toast.info(this.$t('shared.error.server_validation.payment_requires_action') as string);
            return;
          }
        }
        // TouchNet redirect
        else if (this.paymentMethod === PaymentMethod.TouchNet) {
          const ticket = result.errors[''].pop();
          if (ticket) {
            const context = {
              quote: this.quote,
              formData: this.formData,
              billingInfo: this.billingInfo,
              paymentMethod: this.paymentMethod,
              paymentGatewayToken: this.paymentGatewayToken,
              timestamp: this.timestamp,
              couponCode: this.couponCode,
            };
            this.$eventBus.emit('initTouchNetRedirect', ticket.code, JSON.stringify(context));
            this.$toast.info(this.$t('shared.error.server_validation.payment_requires_action') as string);
            return;
          }
        }
      }

      // Remove TouchNet token - the user needs to fill the payment form again
      if (this.paymentMethod === PaymentMethod.TouchNet) {
        this.paymentGatewayToken = null;
      }

      // It is important to show validation errors after form refresh
      this.$nextTick(async () => {
        this.feedErrorBag(result);
        this.scrollToErrors();
        for (const error of this.errors.items) {
          if (!error.field.includes('Billing')) {
            await this.goToFormPage();
            return;
          }
        }
      });
    }
  }

  public async fetchEventAndRefreshForm(): Promise<void> {
    const event = await this.fetchEvent();
    if (!event) return;

    this.form = event.forms.find(x => x.id === this.form.id)!;
    this.refreshTimestamp();
  }

  public async goToFormPage() {
    const target = this.form.type === FormType.Registration ? 'registration' : 'submission';
    await this.$router.push(this.localePath({ name: `slug-${target}`, query: this.$route.query }));
  }

  public async goToBillingPage() {
    const target = this.form.type === FormType.Registration ? 'registration' : 'submission';
    await this.$router.push(this.localePath({ name: `slug-${target}-billing`, query: this.$route.query }));
  }

  public async goToConfirmationPage() {
    const target = this.form.type === FormType.Registration ? 'registration' : 'submission';
    await this.$router.push(this.localePath({ name: `slug-${target}-confirmation`, query: this.$route.query }));
  }

  public async changePage(page: string) {
    if (page === 'form') {
      await this.goToFormPage();
    }
  }

  public onFormUpdate(formData) {
    this.formData = formData;
  }

  public onBillingUpdate(info) {
    this.billingInfo = info;
  }

  public onPaymentMethodUpdate(paymentMethod: PaymentMethod) {
    this.paymentMethod = paymentMethod;
  }

  public refreshTimestamp() {
    this.timestamp = this.$moment.utc().format('YYYY-MM-DD[T]HH:mm:ss');
  }

  public async onPaymentGatewayAuth(authToken: string) {
    this.paymentGatewayAuthToken = authToken;
    await this.nextStep();
  }

  public async onPaymentGatewayTokenFetch(token: string) {
    this.paymentGatewayToken = token;
    await this.nextStep();
  }

  public async onCouponCodeUpdate(couponCode: string): Promise<void> {
    this.couponCode = couponCode;
    const newQuote = await this.fetchNewQuote();
    if (newQuote != null) this.quote = newQuote;
  }

  public onNameOnCardUpdate(nameOnCard: string) {
    this.nameOnCard = nameOnCard;
  }

  public canUserAccessEntry(formEntry: FormEntryDto): boolean {
    if (this.isOrganizer || this.isChair) return true;
    return formEntry.userId === this.$auth.user.id;
  }
}
